import {Injectable} from '@angular/core';
import {ActivatedRoute} from '@angular/router';
import {BehaviorSubject, Observable} from 'rxjs';
import {filter} from 'rxjs/operators';
import {HttpParams} from '@angular/common/http';
import {MatDialog} from '@angular/material/dialog';
import {ErrorModalComponent} from '../errorhandler/error-modal/error-modal.component';
import {JwtHelperService} from '@auth0/angular-jwt';
import {UserData} from './user.model';
import {TenantService} from './tenant.service';

@Injectable({
  providedIn: 'root'
})
export class AuthenticationService {

  private jwtHelperService = new JwtHelperService();

  private authenticatedUserSubject: BehaviorSubject<UserData> = new BehaviorSubject<UserData>(null);
  token: string;
  currentUserData: UserData;

  constructor(private route: ActivatedRoute, private tenantService: TenantService, private matDialog: MatDialog) {
    this.route.fragment.subscribe((fragment: string) => {
      const accessToken = new URLSearchParams(fragment).get('access_token');
      if (accessToken) {
        this.token = accessToken;
        this.currentUserData = this.jwtHelperService.decodeToken(accessToken);
        this.authenticatedUserSubject.next(this.currentUserData);
        window.location.hash = '';
      }
    });
  }

  public get authenticatedUser(): Observable<UserData> {
    return this.authenticatedUserSubject.asObservable().pipe(filter(user => !!user));
  }

  public triggerLogin() {
    this.triggerLoginWithCallback(this.getCurrentUri());
  }

  private getAuthDomain(): string {
    if (window.location.host.endsWith('dev.drklein-plattform.de')) {
      return 'https://id.dev.drklein-plattform.de';
    } else if (window.location.host.endsWith('staging.drklein-plattform.de')) {
      return 'https://id.staging.drklein-plattform.de';
    } else if (window.location.host.endsWith('drklein-plattform.de')) {
      return 'https://id.drklein-plattform.de';
    } else {
      return 'https://id.dev.drklein-plattform.de';
    }
  }

  private pathHasAccessToken() {
    return location.hash.match(new RegExp('access_token=([^&]*)'));
  }

  public getToken(): string {
    if (this.currentUserData && this.currentUserData.exp < new Date().getTime()) {
      this.triggerLogin();
    }
    return this.token;
  }

  triggerLoginWithCallback(callback: string) {
    if (!this.pathHasAccessToken() && !this.token) {
      this.tenantService.getCurrentTenantInfo().subscribe(tenantInfo => {
          let params: HttpParams = new HttpParams();
          params = params.set('response_type', 'token');
          params = params.set('client_id', 'partner-app');
          if (tenantInfo.id) {
            params = params.set('tenant', tenantInfo.id);
          }
          params = params.set('audience', 'EMPLOYEE');
          params = params.set('redirect_uri', callback);
          window.location.href = this.getAuthDomain() +
            '/auth/oauth/authorize?' + params.toString();
        },
        error => this.matDialog.open(ErrorModalComponent, {data: error}));
    }
  }

  passwordChange() {
    let params: HttpParams = new HttpParams();
    params = params.set('return_uri', this.getCurrentUri());
    params = params.set('audience', 'EMPLOYEE');
    window.location.href = this.getAuthDomain() + '/auth/password/change?' + params.toString();
  }

  logout() {
    let params: HttpParams = new HttpParams();
    params = params.set('return_uri', this.getCurrentUri());
    params = params.set('audience', 'EMPLOYEE');
    window.location.href = this.getAuthDomain() + '/auth/logout?' + params.toString();
  }

  getCurrentUri(): string {
    return window.location.href.split('#')[0];
  }

  getUserId() {
    return this.currentUserData.sub;
  }

  getUsername() {
    return this.currentUserData?.user_name;
  }
}
